import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { useThemeStore } from '@/stores/themeStore';
import { useAuthStore } from '@/stores/authStore';
import '@/App.css';

// Pages
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { DashboardPage } from '@/pages/DashboardPage';
import { PeriodDashboardPage } from '@/pages/PeriodDashboardPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { UsersPage } from '@/pages/UsersPage';
import { ZabbixConfigPage } from '@/pages/ZabbixConfigPage';
import { IncidentsPage } from '@/pages/IncidentsPage';
import { IncidentHistoryPage } from '@/pages/IncidentHistoryPage';
import { IncidentBoardPage } from '@/pages/IncidentBoardPage';
import { MaintenancePage } from '@/pages/MaintenancePage';
import { HostsPage } from '@/pages/HostsPage';
import { HostDetailPage } from '@/pages/HostDetailPage';
import { HostGroupsPage } from '@/pages/HostGroupsPage';
import { TopTriggersPage } from '@/pages/TopTriggersPage';
import { AvailabilityPage } from '@/pages/AvailabilityPage';
import { NOCModePage } from '@/pages/NOCModePage';

import { SLAConfigPage } from '@/pages/SLAConfigPage';
import { SavedFiltersPage } from '@/pages/SavedFiltersPage';
import { CategoriesPage } from '@/pages/CategoriesPage';
import { ScheduledReportsPage } from '@/pages/ScheduledReportsPage';
import { GroupsPage } from '@/pages/GroupsPage';
import { OnCallPage } from '@/pages/OnCallPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { StatusPage } from '@/pages/StatusPage';
import { HeatmapPage } from '@/pages/HeatmapPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { AIAgentsPage, AIAnalysisPage, AIGranularPage, AICorrelationPage, AIPredictionPage, AIRCAPage, AIHistoryPage, AIHealthScoresPage, AIAnomaliesPage, AIHistoricalAnalysisPage } from '@/pages/AIOpsPages';
import { KBSolutionsPage } from '@/pages/KBSolutionsPage';
import { KBRecommendationsPage } from '@/pages/KBRecommendationsPage';
import { DailySummaryPage } from '@/pages/DailySummaryPage';
import { SLADashboardPage } from '@/pages/SLADashboardPage';
import { CapacityDashboardPage } from '@/pages/CapacityDashboardPage';
import { ExecutiveDashboardPage } from '@/pages/ExecutiveDashboardPage';
import { MonthlyComparisonPage } from '@/pages/MonthlyComparisonPage';
import { OrgsPage } from '@/pages/OrgsPage';
import { WebMonitorPage } from '@/pages/WebMonitorPage';
import { RadarPage } from '@/pages/RadarPage';
import DomainDossierPage from '@/pages/DomainDossierPage';
import MonitorDetailPage from '@/pages/MonitorDetailPage';

// Super Admin Pages
import { SuperAdminLoginPage } from '@/pages/SuperAdminLoginPage';
import { SuperAdminLayout } from '@/pages/SuperAdminLayout';
import { SuperAdminDashboard } from '@/pages/SuperAdminDashboard';
import { SuperAdminTenantsPage } from '@/pages/SuperAdminTenantsPage';
import { SuperAdminAdminsPage } from '@/pages/SuperAdminAdminsPage';
import { SuperAdminSettingsPage } from '@/pages/SuperAdminSettingsPage';
import { SuperAdminAuditPage } from '@/pages/SuperAdminAuditPage';
import { SuperAdminTenantDetailPage } from '@/pages/SuperAdminTenantDetailPage';
import { SuperAdminPlansPage } from '@/pages/SuperAdminPlansPage';
import { SuperAdminHealthPage } from '@/pages/SuperAdminHealthPage';
import { SuperAdminUsersGlobalPage } from '@/pages/SuperAdminUsersGlobalPage';
import { SuperAdminBillingPage } from '@/pages/SuperAdminBillingPage';
import { SuperAdminWhiteLabelPage } from '@/pages/SuperAdminWhiteLabelPage';

// Layout
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public Route Component (redirect to dashboard if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function App() {
  const { initTheme } = useThemeStore();

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } 
          />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />

          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="incidents" element={<IncidentsPage />} />
            <Route path="incident-board" element={<IncidentBoardPage />} />
            <Route path="incident-history" element={<IncidentHistoryPage />} />
            <Route path="maintenance" element={<MaintenancePage />} />
            <Route path="hostgroups" element={<HostGroupsPage />} />
            <Route path="hosts" element={<HostsPage />} />
            <Route path="hosts/:hostId" element={<HostDetailPage />} />
            <Route path="top-triggers" element={<TopTriggersPage />} />
            <Route path="availability" element={<AvailabilityPage />} />
            <Route path="period" element={<PeriodDashboardPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="orgs" element={<OrgsPage />} />
            <Route path="webmonitor" element={<WebMonitorPage />} />
            <Route path="webmonitor/domains/:domainId" element={<DomainDossierPage />} />
            <Route path="webmonitor/monitors/:monitorId" element={<MonitorDetailPage />} />
            <Route path="radar" element={<RadarPage />} />
            <Route path="status" element={<StatusPage />} />
            <Route path="heatmap" element={<HeatmapPage />} />
            <Route path="sla-dashboard" element={<SLADashboardPage />} />
            <Route path="capacity" element={<CapacityDashboardPage />} />
            <Route path="executive" element={<ExecutiveDashboardPage />} />
            <Route path="monthly" element={<MonthlyComparisonPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="sla-config" element={<SLAConfigPage />} />
            <Route path="saved-filters" element={<SavedFiltersPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
            <Route path="daily-summary" element={<DailySummaryPage />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="oncall" element={<OnCallPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="aiops/agents" element={<AIAgentsPage />} />
            <Route path="aiops/analysis" element={<AIAnalysisPage />} />
            <Route path="aiops/granular" element={<AIGranularPage />} />
            <Route path="aiops/historical" element={<AIHistoricalAnalysisPage />} />
            <Route path="aiops/correlation" element={<AICorrelationPage />} />
            <Route path="aiops/prediction" element={<AIPredictionPage />} />
            <Route path="aiops/rca" element={<AIRCAPage />} />
            <Route path="aiops/history" element={<AIHistoryPage />} />
            <Route path="aiops/health" element={<AIHealthScoresPage />} />
            <Route path="aiops/anomalies" element={<AIAnomaliesPage />} />
            <Route path="kb/solutions" element={<KBSolutionsPage />} />
            <Route path="kb/recommendations" element={<KBRecommendationsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="zabbix-config" element={<ZabbixConfigPage />} />
          </Route>

          {/* NOC Mode - Fullscreen */}
          <Route
            path="/noc"
            element={
              <ProtectedRoute>
                <NOCModePage />
              </ProtectedRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route path="/tenant-super" element={<SuperAdminLoginPage />} />
          <Route path="/tenant-super" element={<SuperAdminLayout />}>
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="tenants" element={<SuperAdminTenantsPage />} />
            <Route path="tenants/:tenantId" element={<SuperAdminTenantDetailPage />} />
            <Route path="admins" element={<SuperAdminAdminsPage />} />
            <Route path="audit" element={<SuperAdminAuditPage />} />
            <Route path="plans" element={<SuperAdminPlansPage />} />
            <Route path="health" element={<SuperAdminHealthPage />} />
            <Route path="all-users" element={<SuperAdminUsersGlobalPage />} />
            <Route path="billing" element={<SuperAdminBillingPage />} />
            <Route path="whitelabel" element={<SuperAdminWhiteLabelPage />} />
            <Route path="settings" element={<SuperAdminSettingsPage />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
